import { useMemo } from "react";
import useContactByPhone from "./useContactByPhone";
import useListingSellerContacts from "./useListingSellerContacts";
import { pickSavedListingSeller } from "./savedListingSeller";
import type { SavedListingSeller } from "./savedListingSeller";

/**
 * WHICH CONTACT OF MINE IS THIS LISTING'S OWNER — asked by the number the listing shows AND by
 * the listing itself. See `savedListingSeller.ts` for the precedence and the three states.
 *
 * ```tsx
 * const saved = useSavedListingSeller(listingId, sellerPhone, isSaveable);
 * // undefined → still asking · null → nobody of mine · { contactId, name, phone, … } → this contact
 * ```
 *
 * Both questions are module stores shared by every reader on the screen (one phone listener /
 * read per number, one batched `POST /contacts/listing-sellers` per page of listings), so the
 * board row, its grid card and the save button beside them asking the same listing cost one
 * answer, not three.
 */
export default function useSavedListingSeller(
  listingId: string | null | undefined,
  phone: string | null | undefined,
  enabled = true
): SavedListingSeller | null | undefined {
  const { exists, contact } = useContactByPhone(phone, enabled);
  const asksListing = Boolean(listingId) && enabled && Boolean(phone);
  const { sellers, failed } = useListingSellerContacts(listingId, asksListing);

  return useMemo(
    () =>
      pickSavedListingSeller({
        phoneAnswer: { exists, contact },
        linked: sellers,
        linkFailed: failed,
        asksListing
      }),
    [exists, contact, sellers, failed, asksListing]
  );
}
